import React from "react";
import { Col, Row } from "react-bootstrap";

import { LineDivider } from "components/layout";
import { useBackendPlatform } from "hooks";
import { backendPlatformsWithFrontendPlatforms } from "mappers";
import {
	GatewayConfigurationOption,
	ProgramDataType,
	TerminalGatewayTypes,
} from "types";
import { float6Normalizer } from "validators";

import TokenizationView from "./tokenizationView";
import { FileBuildView, ProgramView, TransarmorView } from "..";
import TextBlock from "../components/textBlock";

import stylesLayout from "../../../../components/layout/layout.module.scss";

type GatewayData = {
	gatewayConfigurationOption: any;
	gatewayName: { [key: string]: any };
	otherGatewayName: string;
	isLevel2And3DataEnabled: string;
	isCashDiscountEnabled: string;
	isCustomerVaultOrRecurringBilling: string;
	monthlyFee: number;
	transactionFee: number;
	level3PerTransactionFee: number;
	setupFee: number;
	vaultMonthlyFee: number;
	isTransarmorEnabled: string;
	transarmorMonthlyFee: string;
	transarmorPerTransactionFee: string;
	fileBuild: any;
	isTsysTokenizationEnabled: string;
};

type GatewayVirtualTerminalViewProps = {
	data: { gateway: GatewayData } & ProgramDataType;
};

const GatewayVirtualTerminalView = ({
	data,
}: GatewayVirtualTerminalViewProps) => {
	const {
		gatewayConfigurationOption,
		gatewayName,
		otherGatewayName,
		isLevel2And3DataEnabled,
		isCashDiscountEnabled,
		isCustomerVaultOrRecurringBilling,
		monthlyFee,
		transactionFee,
		level3PerTransactionFee,
		setupFee,
		vaultMonthlyFee,
		fileBuild,
	} = data?.gateway || {};
	const isLevel2And3Data = isLevel2And3DataEnabled === "true" ? true : false;
	const isCashDiscount = isCashDiscountEnabled === "true" ? true : false;
	const isCustomerVaultOrRecurringBillingValue =
		isCustomerVaultOrRecurringBilling === "true" ? true : false;
	const {
		isBackEndPlatformOmaha,
		isBackEndPlatformNorth,
		isBackEndPlatformTsys,
	} = useBackendPlatform(data?.backEndPlatform);

	const isGatewayArise =
		gatewayName?.name === TerminalGatewayTypes.ARISE.name;

	const displayFrontEndPlatform =
		backendPlatformsWithFrontendPlatforms.includes(data?.backEndPlatform);

	return (
		<>
			<ProgramView
				data={data}
				displayFrontEndPlatform={displayFrontEndPlatform}
			/>
			<TextBlock
				value={
					GatewayConfigurationOption?.getByName?.(
						gatewayConfigurationOption,
					)?.displayName || ""
				}
				dataAutotest="gatewayConfigurationOption"
			/>
			<Row>
				<Col xl={3}>
					<TextBlock
						label="Name"
						value={gatewayName?.name}
						dataAutotest="gatewayName"
					/>
				</Col>
				{gatewayName?.name === "Other" && (
					<Col xl={3}>
						<TextBlock
							label="Gateway Name"
							value={otherGatewayName}
							dataAutotest="otherGatewayName"
						/>
					</Col>
				)}
			</Row>
			{gatewayName?.name === "RiseOS" && (
				<Row>
					<Col xl={3}>
						<TextBlock
							label="Level 2 & 3 data"
							value={isLevel2And3Data ? "Yes" : "No"}
							dataAutotest="isLevel2And3Data"
						/>
					</Col>
					<Col xl={3}>
						<TextBlock
							label="Cash Discount"
							value={isCashDiscount ? "Yes" : "No"}
							dataAutotest="isCashDiscount"
						/>
					</Col>
				</Row>
			)}
			<TextBlock
				label="Customer Vault / Recurring Billing"
				value={isCustomerVaultOrRecurringBillingValue ? "Yes" : "No"}
				dataAutotest="isCustomerVaultOrRecurringBilling"
			/>
			<Row>
				<Col xl={3}>
					<TextBlock
						label="Monthly Fee"
						value={float6Normalizer(monthlyFee, "$")}
						dataAutotest="monthlyFee"
					/>
				</Col>
				<Col xl={3}>
					<TextBlock
						label="Transaction Fee"
						value={float6Normalizer(transactionFee, "$")}
						dataAutotest="transactionFee"
					/>
				</Col>
				<Col xl={3}>
					<TextBlock
						label="Level 3 Per Transaction Fee"
						value={float6Normalizer(level3PerTransactionFee, "$")}
						dataAutotest="level3PerTransactionFee"
					/>
				</Col>
			</Row>
			<Row>
				<Col xl={3}>
					<TextBlock
						label="Gateway Setup Fee"
						value={float6Normalizer(setupFee, "$")}
						dataAutotest="setupFee"
					/>
				</Col>
				{!isGatewayArise && (
					<Col xl={3}>
						<TextBlock
							label="Vault Monthly Fee"
							value={float6Normalizer(vaultMonthlyFee, "$")}
							dataAutotest="vaultMonthlyFee"
						/>
					</Col>
				)}
			</Row>

			{(isBackEndPlatformOmaha || isBackEndPlatformNorth) && (
				<TransarmorView data={data?.gateway} />
			)}
			{isBackEndPlatformTsys && !isGatewayArise && (
				<TokenizationView data={data?.gateway} />
			)}
			<LineDivider className={`${stylesLayout.lineDividerHolder}`} />
			<h6
				className={`${stylesLayout.viewSectionTitle} ${stylesLayout.subTitle}`}
			>
				File Build Information
			</h6>
			<FileBuildView fileBuild={fileBuild} />
		</>
	);
};

export default GatewayVirtualTerminalView;
